import { useMemo } from "react";
import { getEntityInstanceListNew } from "@/services/entity-instance.service";
import { TriggerOperation } from "@/services/flow.service";
import { useQuery } from "@tanstack/react-query";

export interface TriggerableFlow {
  id: string;
  name: string;
  active: boolean;
}

export const useFlowsByTriggerEntityAndOperation = ({
  entityName,
  operations,
}: {
  entityName: string;
  operations: TriggerOperation[];
}) => {
  return useQuery({
    queryKey: ["flow-by-trigger", entityName, operations],
    queryFn: () =>
      getEntityInstanceListNew({
        entityName: "PyloFlow",
        fieldsToFetch: ["id", "name", "active"],
        filter: {
          query: [
            {
              and: [
                {
                  condition: {
                    field: "trigger.operation",
                    operator: "in",
                    values: operations as Array<string>,
                  },
                },
                {
                  condition: {
                    field: "trigger.entity.name",
                    operator: "equal",
                    value: entityName,
                  },
                },
              ],
            },
          ],
        },
      }),
  });
};

/** The operations that reach a flow when the table runs this mutation. */
const OPERATIONS: Record<"create" | "update" | "delete", TriggerOperation[]> = {
  create: ["create", "create_or_update"],
  update: ["update", "create_or_update"],
  delete: ["delete"],
};

/**
 * The flows a table mutation would fire. Inactive flows are dropped here rather
 * than in the query: the filter's `value` is typed as a string, so a boolean
 * condition would have to be encoded, and the result set is a handful of rows.
 */
export function useFlowTriggerNotice({
  entityName,
  operation,
}: {
  entityName: string;
  operation: "create" | "update" | "delete";
}) {
  const { data } = useFlowsByTriggerEntityAndOperation({
    entityName,
    operations: OPERATIONS[operation],
  });

  const flows = useMemo(
    () =>
      ((data?.entityInstances ?? []) as TriggerableFlow[]).filter((flow) => flow.active !== false),
    [data],
  );

  return { flows, count: flows.length };
}
